import type { LabelDto } from '@keep/shared';
import { Check, Pencil, Plus, Tag, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { useMatch, useNavigate } from 'react-router';
import { useCreateLabel, useDeleteLabel, useLabels, useRenameLabel } from '../../api/labels';
import { ConfirmDialog, Dialog } from '../../components/ui/dialog';
import { IconButton } from '../../components/ui/icon-button';
import { errorMessage } from '../../lib/api';
import { paths } from '../../lib/routes';
import { toastError } from '../../stores/toasts';
import { useUi } from '../../stores/ui';

function LabelRow({ label, onDelete }: { label: LabelDto; onDelete: (label: LabelDto) => void }) {
  const rename = useRenameLabel();
  const [value, setValue] = useState(label.name);
  const [editing, setEditing] = useState(false);

  const save = () => {
    setEditing(false);
    const name = value.trim();
    if (!name || name === label.name) return setValue(label.name);
    rename.mutate(
      { id: label.id, name },
      {
        onError: (error) => {
          setValue(label.name);
          toastError(errorMessage(error));
        },
      },
    );
  };

  return (
    <div className="group flex items-center gap-2 px-3">
      <span className="relative size-10">
        <span className="absolute inset-0 flex items-center justify-center text-muted group-hover:hidden">
          <Tag size={18} />
        </span>
        <IconButton
          icon={Trash2}
          label={`Supprimer le libellé ${label.name}`}
          onClick={() => onDelete(label)}
          className="absolute inset-0 hidden group-hover:inline-flex"
        />
      </span>
      <input
        value={value}
        onChange={(event) => setValue(event.target.value)}
        onFocus={() => setEditing(true)}
        onBlur={save}
        onKeyDown={(event) => {
          if (event.key === 'Enter') event.currentTarget.blur();
          if (event.key === 'Escape') {
            setValue(label.name);
            event.currentTarget.blur();
          }
        }}
        maxLength={50}
        aria-label={`Renommer ${label.name}`}
        className="bare-input h-9 flex-1 border-b border-transparent text-sm focus:border-line"
      />
      <IconButton
        icon={editing ? Check : Pencil}
        label={editing ? 'Enregistrer' : 'Renommer'}
        onMouseDown={(event) => event.preventDefault()}
        onClick={(event) => {
          const input = event.currentTarget.parentElement?.querySelector('input');
          if (editing) input?.blur();
          else input?.focus();
        }}
      />
    </div>
  );
}

export function EditLabelsDialog() {
  const open = useUi((s) => s.labelsDialogOpen);
  const setOpen = useUi((s) => s.setLabelsDialogOpen);
  const { data: labels = [] } = useLabels();
  const create = useCreateLabel();
  const remove = useDeleteLabel();
  const navigate = useNavigate();
  const labelRoute = useMatch('/label/:labelId');
  const [name, setName] = useState('');
  const [toDelete, setToDelete] = useState<LabelDto | null>(null);

  const submit = () => {
    const value = name.trim();
    if (!value) return;
    create.mutate(value, { onSuccess: () => setName('') });
  };

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={setOpen}
        title="Modifier les libellés"
        className="max-w-[300px]"
      >
        <div className="scrollbar-thin flex max-h-[60vh] flex-col overflow-y-auto pb-2">
          <form
            className="flex items-center gap-2 px-3"
            onSubmit={(event) => {
              event.preventDefault();
              submit();
            }}
          >
            <IconButton
              icon={name ? X : Plus}
              label={name ? 'Annuler' : 'Créer un libellé'}
              onClick={() => setName('')}
            />
            <input
              autoFocus
              value={name}
              onChange={(event) => {
                setName(event.target.value);
                create.reset();
              }}
              placeholder="Créer un libellé"
              aria-label="Nom du nouveau libellé"
              maxLength={50}
              className="bare-input h-9 flex-1 border-b border-line text-sm"
            />
            <IconButton
              icon={Check}
              label="Créer le libellé"
              type="submit"
              disabled={!name.trim()}
            />
          </form>
          {create.error && (
            <p className="px-14 pt-1 text-xs text-danger">{errorMessage(create.error)}</p>
          )}
          {labels.map((label) => (
            <LabelRow key={label.id} label={label} onDelete={setToDelete} />
          ))}
        </div>
        <div className="flex justify-end border-t border-line px-3 py-2">
          <button
            type="button"
            className="h-9 rounded px-6 text-sm font-medium hover:bg-hover"
            onClick={() => setOpen(false)}
          >
            OK
          </button>
        </div>
      </Dialog>
      <ConfirmDialog
        open={toDelete !== null}
        onOpenChange={(value) => !value && setToDelete(null)}
        title="Supprimer le libellé ?"
        message="Le libellé sera supprimé et retiré de toutes vos notes. Vos notes ne seront pas supprimées."
        confirmLabel="Supprimer"
        onConfirm={() => {
          if (!toDelete) return;
          remove.mutate(toDelete.id, { onError: (error) => toastError(errorMessage(error)) });
          if (labelRoute?.params.labelId === toDelete.id) navigate(paths.notes);
        }}
      />
    </>
  );
}
